import { useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useNavigate } from 'react-router-dom';

import BoAModal from '#client/components/OpenAccountApplication/components/Steps/ValidationMessage/components/BoAModal';
import { BANKING_CARD_OFFERS } from '#client/constants/bankingCardsOffers';
import { CONFIGURE_BANKING_CARDS_OFFERS } from '#client/constants/experimentsFlags';
import { MODAL_NAMES } from '#client/constants/modals';
import { BANK_OF_AMERICA_OFFER_URL } from '#client/constants/url';
import useBankingEligibilityCheck from '#client/hooks/useBankingEligibilityCheck';
import useModalContext from '#client/hooks/useModalContext';
import useSCorpBankingPrice from '#client/hooks/useSCorpBankingPrice';

export const BLUEVINE_URL =
  'https://www.bluevine.com/partner/zenbusiness-checking?utm_source=zenbusiness-0291763&utm_medium=partner_referral&utm_campaign=site&subid_1=xxx&subid_2=xxx&subid_3=xxx';
export const CHASE_BANK_URL =
  'https://click.linksynergy.com/fs-bin/click?id=A%2fHiQlD3Yhw&offerid=1883754.161&type=3&subid=0';

const useBankingCardOffers = () => {
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const { isCheckingEligibleBankingApplication, isEligibleForBanking } = useBankingEligibilityCheck(
    { isSkip: false }
  );

  const { businessEntityUuid } = useBusinessEntity();
  const { hasSCorpBundle, bankingPrice } = useSCorpBankingPrice();
  const bankingPriceShort = hasSCorpBundle ? '$0/mo' : '$5/mo';

  const cardOffers = [];

  const configureBankingCardsOffers = useExperimentFlag(CONFIGURE_BANKING_CARDS_OFFERS, []);

  const zenbusinessButtonPressed = useButtonPressedCallback({
    elementText: `Buy now for ${bankingPrice}`,
    elementName: 'buy_zb_banking_catbankupsell',
    appPageName: 'category_selling_banking_landing'
  });

  const bluevineButtonPressed = useLinkPressedCallback({
    elementText: 'Apply now',
    elementName: 'apply_bluevine_catbankupsell',
    appPageName: 'category_selling_banking_landing'
  });

  const bankOfAmericaButtonPressed = useButtonPressedCallback({
    elementText: 'View details',
    elementName: 'view_BoA_detals_catbankupsell',
    appPageName: 'category_selling_banking_landing'
  });

  const chaseButtonPressed = useLinkPressedCallback({
    elementText: 'Apply now',
    elementName: 'apply_chase_catbankupsell',
    appPageName: 'category_selling_banking_landing'
  });

  const zenbusinessCard = useMemo(
    () => ({
      id: 'zenbusiness-card',
      logo: {
        src: '/logo/new-zen-logo.svg',
        alt: 'Bluevine Logo',
        desktopWidth: '50%',
        mobileWidth: '40%'
      },
      title: 'Business Banking Account',
      description:
        'Start smart, stay organized, and manage your business finances, all in one place. ',
      features: [
        <>
          <strong>Just {bankingPriceShort}, lower fees</strong> compared to traditional banks, and{' '}
          <strong>no minimum balance to keep</strong>
        </>,
        'Get business in order with a business bank account',
        'Separate business and personal finances for better financial management'
      ],
      ctaButton: {
        text: `Buy now for ${bankingPrice}`,
        onClick: () => {
          zenbusinessButtonPressed();
          navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`);
        }
      }
    }),
    [navigate, zenbusinessButtonPressed, bankingPrice, bankingPriceShort]
  );

  const bluevineCard = useMemo(
    () => ({
      id: 'bluevine-card',
      logo: {
        src: '/s2/vector/bluevine-blue-logo.svg',
        alt: 'Bluevine Logo',
        desktopWidth: '50%',
        mobileWidth: '40%'
      },
      title: 'Business Checking Account',
      description:
        '6 months free of Bluevine Plus and the ability to earn a $300 signup bonus with qualifying activities.',
      features: [
        '$300 signup bonus when meeting qualifying activities',
        'Earn up to 2.7% APY on balances up to $250,000',
        'Unlimited transactions, no overdraft fees, and no fees on ACH payments',
        'Add up to 10 sub accounts each with designated account numbers',
        'Get up to $3 million in FDIC insurance'
      ],
      ctaButton: {
        text: 'Apply now',
        icon: '/s2/icon/external-link-fill.svg',
        onClick: () => {
          bluevineButtonPressed();
          window.open(BLUEVINE_URL, '_blank');
        }
      }
    }),
    [bluevineButtonPressed]
  );

  const bankOfAmericaCard = useMemo(
    () => ({
      id: 'bank-of-america-card',
      logo: {
        src: '/logo/Bank-of-America-Logo.svg',
        alt: 'Bank of America Logo',
        desktopWidth: '80%',
        mobileWidth: '90%'
      },
      title: 'Business Advantage Banking',
      description: 'Earn bonuses totaling $300 to $800 with qualifying activities.',
      features: [
        '$300 checking bonus offer*',
        '$500 credit card cash rewards bonus offer*',
        'Separate your business and personal accounts',
        'Track business expenses more easily'
      ],
      disclaimer: {
        anchorText: '*See offer page for details',
        hyperlink: BANK_OF_AMERICA_OFFER_URL
      },
      disclosure: 'Deposit products are provided by Bank of America, N.A. Member FDIC.',
      ctaButton: {
        text: 'View details',
        onClick: () => {
          bankOfAmericaButtonPressed();
          openModal({ name: 'BoA Modal', component: BoAModal, isOpen: true });
        }
      }
    }),
    [bankOfAmericaButtonPressed, openModal]
  );

  const chaseCard = useMemo(
    () => ({
      id: 'chase-card',
      logo: {
        src: '/s2/vector/chase-for-business.svg',
        alt: 'Chase Logo',
        desktopWidth: '40%',
        mobileWidth: '30%'
      },
      title: 'Business Checking Account',
      description: 'Open an account and earn up to a $500 bonus with qualifying activities.',
      features: [
        'Multiple ways to pay and get paid, like built-in invoicing and card acceptance, Zelle®, and Tap to Pay on iPhone, to help improve your cash flow',
        'Access business insights and tools to help you grow',
        'Personal attention with a large scale presence — more than 4,900 branches and 15,400 ATMs',
        'Multiple ways to waive the $15 monthly service fee'
      ],
      ctaButton: {
        text: 'Apply now',
        icon: '/s2/icon/external-link-fill.svg',
        onClick: () => {
          chaseButtonPressed();
          window.open(`${CHASE_BANK_URL}&u1=${businessEntityUuid}`, '_blank');
        }
      }
    }),
    [chaseButtonPressed, businessEntityUuid]
  );

  const cardRegistry = {
    [BANKING_CARD_OFFERS.BLUEVINE]: bluevineCard,
    [BANKING_CARD_OFFERS.BANK_OF_AMERICA]: bankOfAmericaCard,
    [BANKING_CARD_OFFERS.CHASE]: chaseCard
  };

  configureBankingCardsOffers.forEach((id) => {
    if (id === 'zenbusiness' && isEligibleForBanking) {
      cardOffers.push(zenbusinessCard);
      return;
    }

    const card = cardRegistry[id];

    if (card) {
      cardOffers.push(card);
    }
  });

  return { loading: isCheckingEligibleBankingApplication, bankingCards: cardOffers };
};

export default useBankingCardOffers;
